import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

type Props = {
  autoplay?: number | false;         // ms, z.B. 3000; false = aus
  infinite?: boolean;                // loop
  showArrows?: boolean;
  showDots?: boolean;
  className?: string;
  slideClassName?: string;
  onSlideChange?: (index: number) => void;
  children: React.ReactNode;
};

export const AnimatedSlider: React.FC<Props> = ({
  autoplay = 0,
  infinite = true,
  showArrows = true,
  showDots = true,
  className,
  slideClassName,
  onSlideChange,
  children
}) => {
  const plugins = useMemo(
    () => (autoplay ? [Autoplay({ delay: autoplay, stopOnInteraction: true })] : []),
    [autoplay]
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: infinite, align: "center" },
    plugins
  );

  const slides = React.Children.toArray(children);
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState<number[]>([]);
  const slideNodesRef = useRef<HTMLDivElement[]>([]);

  const setSlideNode = (el: HTMLDivElement | null, i: number) => {
    if (el) slideNodesRef.current[i] = el;
  };

  const applyStateClasses = useCallback((api: any) => {
    const idx = api.selectedScrollSnap();
    setSelected(idx);
    slideNodesRef.current.forEach((node, i) => {
      if (!node) return;
      node.classList.remove("is-before", "is-active", "is-after");
      if (i < idx) node.classList.add("is-before");
      else if (i === idx) node.classList.add("is-active");
      else node.classList.add("is-after");
    });
    onSlideChange?.(idx);
  }, [onSlideChange]);

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    applyStateClasses(emblaApi);
    emblaApi.on("select", () => applyStateClasses(emblaApi));
    emblaApi.on("reInit", () => applyStateClasses(emblaApi));
  }, [emblaApi, applyStateClasses]);

  return (
    <div className={`embla ${className ?? ""}`}>
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((c, i) => (
            <div
              className={`embla__slide ${slideClassName ?? ""}`}
              key={i}
              ref={el => setSlideNode(el, i)}
            >
              {c}
            </div>
          ))}
        </div>
      </div>

      {showArrows && (
        <>
          <button
            className="embla__prev"
            onClick={() => emblaApi?.scrollPrev()}
            aria-label="Previous slide"
          >
            ‹
          </button>
          <button
            className="embla__next"
            onClick={() => emblaApi?.scrollNext()}
            aria-label="Next slide"
          >
            ›
          </button>
        </>
      )}

      {showDots && (
        <div className="embla__dots">
          {snaps.map((_, i) => (
            <button
              key={i}
              className={`embla__dot ${i === selected ? "is-selected" : ""}`}
              onClick={() => emblaApi?.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
